import React, { useState } from 'react';
import { Upload, FileSpreadsheet, X, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from "../context/AuthProvider";
import { BASE_URL } from '../apiConfig';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

export default function ImportOrdersGoogleSheets({ isOpen, onClose, fetchOrders }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Check if file is Excel/CSV
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV');
      return;
    }

    setFile(selectedFile);
    setPreviewData(null);
    setImportResult(null);

    // Preview file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        if (jsonData.length > 0) {
          setPreviewData({
            headers: jsonData[0],
            rows: jsonData.slice(1, 6) // Show first 5 rows
          });
        }
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error('Erreur lors de la lecture du fichier');
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setIsLoading(true);
    setImportResult(null);

    try {
      // Read file
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          // Send to backend
          const response = await fetch(`${BASE_URL}/api/import-orders-google-sheets`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ orders: jsonData }),
          });

          const result = await response.json();

          if (result.code === 200) {
            setImportResult({
              success: true,
              total: result.total || jsonData.length,
              created: result.created || 0,
              failed: result.failed || 0,
              errors: result.errors || []
            });
            toast.success(`Import réussi: ${result.created || 0} commande(s) créée(s)`);
            if (fetchOrders) {
              fetchOrders();
            }
          } else {
            setImportResult({
              success: false,
              message: result.message || 'Erreur lors de l\'import',
              errors: result.errors || []
            });
            toast.error(result.message || 'Erreur lors de l\'import');
          }
        } catch (error) {
          console.error('Error processing file:', error);
          toast.error('Erreur lors du traitement du fichier');
          setImportResult({
            success: false,
            message: 'Erreur lors du traitement du fichier'
          });
        } finally {
          setIsLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Erreur lors de l\'import');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData(null);
    setImportResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Importer des commandes depuis Google Sheets</h3>
              <p className="text-sm text-gray-500">Téléchargez votre fichier Excel ou CSV</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
        
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner un fichier
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                disabled={isLoading}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-12 h-12 text-gray-400 mb-3" />
                <span className="text-sm text-gray-600 mb-1">
                  Cliquez pour télécharger ou glissez-déposez
                </span>
                <span className="text-xs text-gray-500">
                  Formats acceptés: .xlsx, .xls, .csv
                </span>
              </label>
              {file && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-blue-600">
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>{file.name}</span>
                </div>
              )}
            </div>
          </div>

          {importResult && (
            <div className={`mb-6 px-4 py-2 rounded-lg border ${
              importResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {importResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  {importResult.success ? (
                    <>
                      <p className="text-sm font-medium text-green-900 mb-1">
                        Import terminé avec succès
                      </p>
                      <div className="text-xs text-green-700 space-y-1">
                        <p>Total: {importResult.total} ligne(s)</p>
                        <p>Créées: {importResult.created} commande(s)</p>
                        {importResult.failed > 0 && (
                          <p className="text-red-600">Échouées: {importResult.failed}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm font-medium text-red-900">
                      {importResult.message}
                    </p>
                  )}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-1 max-h-40 overflow-y-auto">
                      <p className="text-xs font-medium text-red-700 mb-1">Erreurs:</p>
                      <ul className="text-[11px] text-red-600 space-y-1">
                        {importResult.errors.slice(0, 10).map((error, idx) => (
                          <li key={idx}>• {error}</li>
                        ))}
                        {importResult.errors.length > 10 && (
                          <li className="text-gray-500">
                            ... et {importResult.errors.length - 10} autre(s) erreur(s)
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          {previewData && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Aperçu des données</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        {previewData.headers.map((header, idx) => (
                          <th key={idx} className="px-3 py-2 text-left font-medium text-gray-700">
                            {header || `Colonne ${idx + 1}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.rows.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {previewData.headers.map((_, colIdx) => (
                            <td key={colIdx} className="px-3 py-2 text-gray-600">
                              {row[colIdx] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Affichage des 5 premières lignes
              </p>
            </div>
          )}

          {/* Format Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              Format requis pour Google Sheets
            </h4>
            <p className="text-xs text-blue-700 mb-2">
              Assurez-vous que votre fichier contient les colonnes suivantes:
            </p>
            <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
              <li>client_name (Nom du client) - <strong>Obligatoire</strong></li>
              <li>client_phone (Téléphone du client) - <strong>Obligatoire</strong></li>
              <li>client_address (Adresse du client) - <strong>Obligatoire</strong></li>
              <li>client_city (Ville du client) - <strong>Obligatoire</strong> - Le prix de livraison sera automatiquement récupéré depuis la base de données</li>
              <li>product_id (ID du produit) - <strong>Obligatoire</strong></li>
              <li>product_quantity (Quantité) - <strong>Obligatoire</strong></li>
              <li>selling_price_per_unit (Prix de vente unitaire) - <strong>Obligatoire</strong></li>
              <li>Variantes - <strong>Optionnel</strong> - Utilisez des colonnes nommées selon le type de variante (ex: color, size, material). Vous pouvez avoir plusieurs variantes par produit.</li>
              <li>comment (Commentaire) - Optionnel</li>
            </ul>
            <p className="text-xs text-blue-500 mt-2">
              <strong>Note:</strong> Le vendeur (saller_id) sera automatiquement défini comme l'utilisateur qui importe le fichier. 
              Le prix original du vendeur (original_saller_price) sera automatiquement récupéré depuis la base de données (product_prices.saller_price).
              Les commandes peuvent contenir plusieurs produits - ajoutez une ligne par produit pour le même client.
              <br />
              <strong>Variantes multiples:</strong> Pour spécifier plusieurs variantes (ex: color et size), créez des colonnes nommées "color", "size", etc. avec leurs valeurs respectives.
              <br />
              <strong className="text-red-600">⚠️ Validation stricte:</strong> Les variantes doivent correspondre exactement aux groupes de variantes définis pour chaque produit. Si une validation échoue, l'import complet sera arrêté.
            </p>
            <p className="text-xs text-blue-500 mt-2">
              Consultez le fichier de documentation pour plus de détails sur le format.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {importResult?.success ? 'Fermer' : 'Annuler'}
          </button>
          {!importResult?.success && (
            <button
              onClick={handleImport}
              disabled={!file || isLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Import en cours...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Importer</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

